import {HtmlSignature, HtmlSignatureFull} from "../types/htmlSignatures";
import {Paginated, QueryParams, removeNullAnUndefined} from "../utils/commons";
import {apiCall, HttpMethod} from "./index";
import {DataSignature, DataSignatureFull} from "../types/dataSignatures";
import {AesSignature, AesSignatureFull, PdfSignature, PdfSignatureFull} from "../types/pdfSignatures";
import {Timestamp} from "../types/timestamps";
import {store} from "../index";
import {setAlert} from "@kopjra/uikit";
import {I18n} from "react-redux-i18n";

export async function getHtmlSignatures(query: QueryParams<HtmlSignature>): Promise<Paginated<HtmlSignature>> {
    const res = await apiCall({
        urlComponent: "/interactions",
        query: {...query, emptyScope: true},
    });
    return await res.json();
}

export async function getHtmlDataSignature(id: number): Promise<HtmlSignatureFull | DataSignatureFull> {
    const res = await apiCall({
        urlComponent: `/interactions/${id}`,
    });
    return await res.json();
}

export async function getDataSignatures(query: QueryParams<DataSignature>): Promise<Paginated<DataSignature>> {
    const res = await apiCall({
        urlComponent: "/interactions",
        query: {...query, emptyScope: true},
    });
    return await res.json();
}

export async function getTimestamps(query: QueryParams<Timestamp>): Promise<Paginated<Timestamp>> {
    const res = await apiCall({
        urlComponent: "/timestamps",
        query,
    });
    return await res.json();
}

export async function getPdfSignatures(query: QueryParams<PdfSignature>): Promise<Paginated<PdfSignature>> {
    const res = await apiCall({
        urlComponent: "/signatures",
        query: {...query, emptyScope: true},
    });
    const json = await res.json();
    return {
        total: json.total,
        items: json.items.map(toPdfSignature),
    };
}

export async function getAesSignatures(query: QueryParams<AesSignature>): Promise<Paginated<AesSignature>> {
    const res = await apiCall({
        urlComponent: "/aesSignatures",
        query: {...query, emptyScope: true},
    });
    const json = await res.json();
    return {
        total: json.total,
        items: json.items.map(toAesSignature),
    };
}

function toPdfSignature(obj: any): PdfSignature {
    let result: PdfSignature = {
        id: obj.id,
        archetypeId: obj.archetypeId,
        createdAt: obj.createdAt,
        currentStatus: obj.currentStatus,
        deletedAt: obj.deletedAt,
        deletedEvidence: obj.deletedEvidence,
        downloadableUntil: obj.downloadableUntil,
        expirationPeriod: obj.expirationPeriod,
        expiresAt: obj.expiresAt,
        otpSms: obj.otp?.sms,
        otpEmail: obj.otp?.email,
        signatureType: obj.signatureType,
        signerFullname: obj.signerFullname,
        tags: obj.tags,
        updatedAt: obj.updatedAt,
        userLandedAt: obj.userLandedAt,
        userTookoffAt: obj.userTookoffAt,
        isForensic: obj.isForensic,
        documents: obj.documents,
    };
    return removeNullAnUndefined(result);
}

function toAesSignature(obj: any): AesSignature {
    let result: AesSignature = {
        ...toPdfSignature(obj),
        aesProviderId: obj.aesProviderId,
    };
    return removeNullAnUndefined(result);
}

function toPdfSignatureFull(obj: any): PdfSignatureFull {
    return {
        ...toPdfSignature(obj),
        auditTrail: obj.auditTrail,
        auditTrailXML: obj.auditTrailXML,
        attachedFiles: obj.attachedFiles,
    }
}

function toAesSignatureFull(obj: any): AesSignatureFull {
    return {
        ...toAesSignature(obj),
        auditTrail: obj.auditTrail,
        auditTrailXML: obj.auditTrailXML,
        attachedFiles: obj.attachedFiles,
    }
}

export async function getPdfSignature(id: number): Promise<PdfSignatureFull> {
    const res = await apiCall({
        urlComponent: `/signatures/${id}`,
    });
    return toPdfSignatureFull(await res.json());
}

export async function getAesSignature(id: number, aid: number): Promise<AesSignatureFull> {
    const res = await apiCall({
        urlComponent: `aesProviders/${aid}/aesSignatures/${id}`,
    });
    return toAesSignatureFull(await res.json());
}

export async function postTimestamp(file: File, trusted: boolean): Promise<void> {
    try {
        const res = await apiCall({
            urlComponent: "/timestamps",
            method: HttpMethod.POST,
            body: file,
            bodyRaw: true,
            contentType: "application/octet-stream",
            query: {
                fileName: file.name,
                trusted,
                sync: true,
            }
        });
        if (!res.ok) {
            store.dispatch(setAlert(I18n.t("error.wrongFilename"), "error"));
        } else {
            const blob = await res.blob();
            const blobFile = window.URL.createObjectURL(blob);
            const aTag = document.createElement("a");
            aTag.href = blobFile;
            aTag.download = `${file.name}.${trusted ? "tsd" : "ots"}`;
            document.body.appendChild(aTag);
            aTag.click();
            URL.revokeObjectURL(blobFile);
            aTag.remove();
        }
    } catch (error) {
        store.dispatch(setAlert(I18n.t("error.wrongFilename"), "error"));
    }
}
